import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import axios from 'axios';

const SpecializationSelection = ({ industry, value, onChange }) => {
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (industry && industry.slug) {
      fetchSpecializations();
    } else {
      setSpecializations([]);
      onChange(null);
    }
  }, [industry?.slug]);

  const fetchSpecializations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching specializations for:', industry.slug);
      const response = await axios.get(`/api/industries/${industry.slug}/specializations`);
      
      console.log('Specializations response:', response.data);
      
      if (response.data.success && response.data.specializations) {
        // Convert specializations object to array
        const specializationsArray = Object.entries(response.data.specializations).map(([key, spec]) => ({
          slug: key,
          name: spec.name || key,
          description: spec.description || '',
          terminology: spec.terminology || {}
        }));
        
        console.log('Parsed specializations:', specializationsArray);
        setSpecializations(specializationsArray);
        
        // Auto-select 'general' if available and nothing selected
        if (!value) {
          const general = specializationsArray.find(s => s.slug === 'general');
          if (general) {
            onChange(general.slug);
          }
        }
      } else {
        setError('No specializations found');
      }
    } catch (err) {
      setError('Failed to load specializations');
      console.error('Error fetching specializations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything if no industry
  if (!industry || !industry.slug) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2} my={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading specializations...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  // If no specializations, don't show
  if (specializations.length === 0) {
    return null;
  }

  const selectedSpec = specializations.find(s => s.slug === value);

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Choose Your Specialization
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the specific focus area for your business
      </Typography>

      <FormControl fullWidth>
        <InputLabel id="specialization-select-label">Specialization</InputLabel>
        <Select
          labelId="specialization-select-label"
          id="specialization-select"
          value={value || ''}
          label="Specialization"
          onChange={(e) => onChange(e.target.value)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 400,
              },
            },
          }}
        >
          {specializations.map((spec) => (
            <MenuItem key={spec.slug} value={spec.slug}>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {spec.name}
                </Typography>
                {spec.description && (
                  <Typography variant="caption" color="text.secondary">
                    {spec.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedSpec && selectedSpec.terminology && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Customized terminology for your theme:
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {selectedSpec.terminology.team && (
              <Chip label={`Team: ${selectedSpec.terminology.team}`} size="small" />
            )}
            {selectedSpec.terminology.services && (
              <Chip label={`Services: ${selectedSpec.terminology.services}`} size="small" />
            )}
            {selectedSpec.terminology.contact && (
              <Chip label={`Contact: ${selectedSpec.terminology.contact}`} size="small" />
            )}
            {selectedSpec.terminology.portal && (
              <Chip label={`Portal: ${selectedSpec.terminology.portal}`} size="small" />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SpecializationSelection;